/**
 * Cookie Storage Utilities
 * 
 * Provides secure cookie storage for auth tokens
 * Fallback to encrypted localStorage if cookies are disabled
 */

import type { TokenInfo } from '../services/types';

const AUTH_TOKEN_COOKIE = 'auth_token';
const REFRESH_TOKEN_COOKIE = 'refresh_token';

/**
 * Check if cookies are available and enabled
 */
export function areCookiesAvailable(): boolean {
  try {
    // Try to set and remove a test cookie
    const test = 'cookie_test';
    document.cookie = `${test}=${test}; SameSite=Strict; Secure; path=/`;
    const available = document.cookie.indexOf(test) !== -1;
    // Clean up
    document.cookie = `${test}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    return available;
  } catch {
    return false;
  }
}

/**
 * Set a secure cookie
 */
function setCookie(name: string, value: string, days: number = 7): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  // Set with secure flags
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Strict; Secure`;
}

/**
 * Get a cookie value
 */
function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Save auth info to cookies
 * @param tokenInfo Auth info to save
 * @param rememberMe Whether to persist for 7 days (true) or session only (false)
 */
export function saveTokenInfoToCookies(tokenInfo: TokenInfo, rememberMe: boolean = false): boolean {
  try {
    const days = rememberMe ? 7 : 1; // 1 day for session, 7 days for remember me
    
    setCookie(AUTH_TOKEN_COOKIE, tokenInfo.accessToken, days);
    setCookie(REFRESH_TOKEN_COOKIE, tokenInfo.refreshToken, days);
    
    // console.log('✅ Auth info saved to cookies');
    return true;
  } catch (error) {
    console.error('Failed to save auth info to cookies:', error);
    return false;
  }
}

/**
 * Get auth info from cookies
 */
export function getTokenInfoFromCookies(): TokenInfo | null {
  try {
    const accessToken = getCookie(AUTH_TOKEN_COOKIE);
    const refreshToken = getCookie(REFRESH_TOKEN_COOKIE);
    
    if (!accessToken || !refreshToken) {
      return null;
    }
    
    return {
      accessToken,
      refreshToken,
    };
  } catch (error) {
    console.error('Failed to get auth info from cookies:', error);
    return null;
  }
}

/**
 * Update auth info in cookies (preserves expiry)
 */
export function updateTokenInfoInCookies(tokenInfo: TokenInfo): boolean {
  try {
    // Update with same expiry as before (check if refresh token exists to determine if it's persistent)
    const isRemembered = !!getCookie(REFRESH_TOKEN_COOKIE);
    const days = isRemembered ? 7 : 1;
    
    setCookie(AUTH_TOKEN_COOKIE, tokenInfo.accessToken, days);
    setCookie(REFRESH_TOKEN_COOKIE, tokenInfo.refreshToken, days);
    
    // console.log('✅ Auth info updated in cookies');
    return true;
  } catch (error) {
    console.error('Failed to update auth info in cookies:', error);
    return false;
  }
}

/**
 * Clear auth info from cookies
 */
export function clearTokenInfoFromCookies(): void {
  try {
    deleteCookie(AUTH_TOKEN_COOKIE);
    deleteCookie(REFRESH_TOKEN_COOKIE);
    // console.log('🗑️  Auth info cleared from cookies');
  } catch (error) {
    console.error('Failed to clear auth info from cookies:', error);
  }
}

